import React, { useEffect, useState } from "react";
import { HiOutlineChartBar } from "react-icons/hi2";
import StatCard from "./StatCard";
import TokenUsageChart from "./TokenUsageChart";
import UsersTable from "./UsersTable";
import UserHistoryModal from "./UserHistoryModal";
import { fetchUsageDashboard } from "./api";
import { transformStatCards, transformTokenUsageTrend, transformUsers } from "./transform";

const AiStatsDashboard = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [statCards, setStatCards] = useState([]);
  const [tokenUsageTrend, setTokenUsageTrend] = useState([]);
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    fetchUsageDashboard()
      .then((data) => {
        if (cancelled) return;
        setStatCards(transformStatCards(data.stat_cards));
        setTokenUsageTrend(transformTokenUsageTrend(data.token_usage_trend));
        setUsers(transformUsers(data.users));
        setStatus("loaded");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br mt-24 from-indigo-50 via-gray-50 to-emerald-50 px-4 md:px-10 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Usage Dashboard</h1>
            <p className="text-gray-500 font-medium mt-1">
              Token and credit usage across all subscribers
            </p>
          </div>
          <span className="inline-flex items-center gap-2 bg-white border-2 border-emerald-200 shadow-sm rounded-full px-4 py-1.5 text-sm font-semibold text-gray-700">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
            Live &middot; updated just now
          </span>
        </div>

        {status === "error" && (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 rounded-2xl px-6 py-4 mb-6 font-medium">
            Couldn't load usage data. Refresh the page to try again.
          </div>
        )}

        <div className="flex flex-wrap gap-4 mb-6">
          {status === "loading"
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-gray-200 bg-white shadow-sm px-6 pt-6 pb-5 flex-1 min-w-220 h-140 animate-pulse"
                />
              ))
            : statCards.map((card) => <StatCard key={card.key} {...card} />)}
        </div>

        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm px-6 py-6">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-indigo-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
              <HiOutlineChartBar size={18} aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Token usage</h2>
              <p className="text-gray-500 font-medium text-sm">
                Input vs. output tokens across all users
              </p>
            </div>
          </div>
          {status === "loaded" && <TokenUsageChart data={tokenUsageTrend} />}
        </div>

        <div className="mt-6">
          <UsersTable users={users} onSelectUser={setSelectedUser} />
        </div>
      </div>

      <UserHistoryModal user={selectedUser} onClose={() => setSelectedUser(null)} />
    </div>
  );
};

export default AiStatsDashboard;
